import React, { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';

type AllowedValues = string | number | boolean | Date | null | undefined | React.ReactNode;

type AllowedValuesWithChunks = AllowedValues | ((chunks: React.ReactNode) => React.ReactNode);
export type TranslateFunction = ((
	id: string, 
	defaultMessageOrValues?: string | Record<string, AllowedValuesWithChunks>, 
	values?: Record<string, AllowedValuesWithChunks>
) => string) & {
	/**
	 * Allows using a different base key from the one provided to hook.
	 * IMPORTANT: make sure you memoize the result of this function if used as a dependency for other hooks.
	 */
  withBaseKey: (baseKey: string) => TranslateFunction;
};

/**
 * @param baseKey a string to be prepended to the message key. 
 * A dot (".") is automatically added to the end of the string if not provided.
 * @returns a function that can be used to access the language file. Eg:
 * ```
 * 	const t = useTranslation();
 *  const title = t('Messages.Title', 'Hello {name}`, {name: 'User'});
 * ```
 * 
 * You can also define a base Key path to be prepended to the language key id, Eg:
 * 
 * ```
 * 	const t = useTranslation('Interface.Messages.Module1');
 *  // This is the same as ('Interface.Messages.Module1.Title')
 *  const title = t('Title', 'Hello {name}`, {name: 'User'});	
 * ```
 */
const useTranslation = (baseKey?: string): TranslateFunction => {
	const intl = useIntl();
	const translate = useMemo(() => {
		return getTranslationFunction(intl, baseKey);
	}, [baseKey, intl]);

	return translate;
};



export const getTranslationFunction = (intl: IntlShape, baseKey?: string): TranslateFunction => {
	const formattedBase = baseKey ? baseKey.endsWith('.') ? baseKey : `${baseKey}.` : '';

	const translate = (id: string, defaultMessageOrValues?: string | Record<string, AllowedValuesWithChunks>, values?: Record<string, AllowedValuesWithChunks>): string => {
		const defaultMessage = typeof defaultMessageOrValues === 'string' ? defaultMessageOrValues : undefined;
		let valuesToUse = typeof defaultMessageOrValues === 'object' ? defaultMessageOrValues : values;

		// bold helper 
		valuesToUse = {
			...valuesToUse,
			b: (chunks: any) => React.createElement('strong', {}, chunks),
		};

		const translation = intl.formatMessage({
			id: `${formattedBase}${id}`,
			defaultMessage: defaultMessage || `${formattedBase}${id}`
		}, valuesToUse || {});

		return translation as unknown as string;
	};

	translate.withBaseKey = (baseKey: string) => {
		return getTranslationFunction(intl, baseKey);
	};

	return translate as TranslateFunction;
};

export default useTranslation;
